import { memo, useCallback, useMemo } from 'react';

import { Checkbox, Divider, ImageCard, Input, Select, Text } from '@desygner/ui-common-components';

import { useTableOfContentContext } from '../TableOfContent.context';
import {
  StyledTocBody,
  StyledTocCheckboxItem,
  StyledTocDivider,
  StyledTocInnerBody,
  StyledTocInput,
  StyledTocItem,
  StyledTocPreview,
  StyledTocPreviewContainer,
  StyledTocSelect,
} from '../TableOfContent.style';
import { TableOfContentLeaderStyle } from '../TableOfContent.type';
import type {
  LeaderOption,
  TableOfContentLabel,
  TableOfContentPreview,
} from '../TableOfContent.type';

interface TableOfContentBodyProps {
  leaderOptions: LeaderOption[];
  label: TableOfContentLabel;
  previewSource: TableOfContentPreview;
  maxContentLevels: number;
}

export const TableOfContentBody = memo(function TableOfContentBody({
  leaderOptions,
  label,
  previewSource,
  maxContentLevels = 5,
}: TableOfContentBodyProps): React.ReactElement {
  const {
    leaderStyle,
    tableFormat,
    isAlignRight,
    isPageNumber,
    isLinkUsed,
    setIsPageNumber,
    setIsLinkUsed,
    setIsAlignRight,
    setLevels,
    setTableFormat,
    setLeaderStyle,
  } = useTableOfContentContext();

  const selectOptions = useMemo(() => {
    return leaderOptions.map(({ value, label }) => {
      return {
        value,
        label,
        isSelected: value === leaderStyle,
      };
    });
  }, [leaderOptions, leaderStyle]);

  const onLeaderStyleChange = useCallback(
    (value: string) => {
      setLeaderStyle(value as TableOfContentLeaderStyle);
    },
    [setLeaderStyle]
  );

  const onLevelInputChange = useCallback(
    (value: string) => {
      setLevels(Number(value));
    },
    [setLevels]
  );

  const onClassicFormatClick = useCallback(() => {
    setTableFormat('classic');
    setIsAlignRight(true);
    setIsPageNumber(true);
    setIsLinkUsed(true);
    setLeaderStyle('none');
  }, [setIsAlignRight, setIsLinkUsed, setIsPageNumber, setLeaderStyle, setTableFormat]);

  const onFormalFormatClick = useCallback(() => {
    setTableFormat('formal');
    setIsAlignRight(true);
    setIsPageNumber(true);
    setIsLinkUsed(true);
    setLeaderStyle('dots');
  }, [setIsAlignRight, setIsLinkUsed, setIsPageNumber, setLeaderStyle, setTableFormat]);

  const onSimpleFormatClick = useCallback(() => {
    setTableFormat('simple');
    setIsAlignRight(false);
    setIsPageNumber(true);
    setIsLinkUsed(true);
    setLeaderStyle('none');
  }, [setIsAlignRight, setIsLinkUsed, setIsPageNumber, setLeaderStyle, setTableFormat]);

  const classicStyle = useMemo(() => {
    return { ...styles.details, selected: tableFormat === 'classic' };
  }, [tableFormat]);

  const formalStyle = useMemo(() => {
    return { ...styles.details, selected: tableFormat === 'formal' };
  }, [tableFormat]);

  const simpleStyle = useMemo(() => {
    return { ...styles.details, selected: tableFormat === 'simple' };
  }, [tableFormat]);

  return (
    <StyledTocBody>
      <StyledTocInnerBody>
        <StyledTocPreview>
          <StyledTocPreviewContainer>
            <ImageCard
              onClick={onClassicFormatClick}
              details={classicStyle}
              source={previewSource.classic}
            />
          </StyledTocPreviewContainer>

          <StyledTocPreviewContainer>
            <ImageCard
              onClick={onFormalFormatClick}
              details={formalStyle}
              source={previewSource.formal}
            />
          </StyledTocPreviewContainer>

          <StyledTocPreviewContainer>
            <ImageCard
              onClick={onSimpleFormatClick}
              details={simpleStyle}
              source={previewSource.simple}
            />
          </StyledTocPreviewContainer>
        </StyledTocPreview>

        <StyledTocDivider>
          <Divider />
        </StyledTocDivider>

        <StyledTocItem>
          <Text size="Body">{label.leaderStyle}</Text>
          <StyledTocSelect>
            <Select options={selectOptions} onChange={onLeaderStyleChange} />
          </StyledTocSelect>
        </StyledTocItem>

        <StyledTocItem>
          <Text size="Body">{label.level}</Text>
          <StyledTocInput>
            <Input
              id="toc-level"
              name="toc-level"
              size="Small"
              type="number"
              value={1}
              min={1}
              max={maxContentLevels}
              onChange={onLevelInputChange}
            />
          </StyledTocInput>
        </StyledTocItem>

        <StyledTocCheckboxItem>
          <Text size="Body">{label.showPageNumber}</Text>
          <Checkbox checked={isPageNumber} onClick={setIsPageNumber} />
        </StyledTocCheckboxItem>

        <StyledTocCheckboxItem>
          <Text size="Body">{label.alignRight}</Text>
          <Checkbox checked={isAlignRight} onClick={setIsAlignRight} />
        </StyledTocCheckboxItem>

        <StyledTocCheckboxItem>
          <Text size="Body">{label.useLink}</Text>
          <Checkbox checked={isLinkUsed} onClick={setIsLinkUsed} />
        </StyledTocCheckboxItem>
      </StyledTocInnerBody>
    </StyledTocBody>
  );
});

const styles = {
  details: {
    maxHeight: 90,
  },
};
